import { StateParams, StateService } from "@uirouter/angularjs";
import {
  MultipleMachineChangeChildStateServiceProvider,
  vsMultiProviderName as MultipleMachineChangeProviderName,
  SingleMachineChangeChildStateServiceProvider,
  vsSingleProviderName as SingleMachineChangeProviderName,
} from "../../shared/components/machineChange";
import { vsComponentName as MachinesDecommissionViewComponentName } from "../views/machines/decommission";
import {
  CloneAndConversionServiceGateway,
  vsName as CloneAndConversionServiceName,
} from "../services/cloneAndConversionService";
import { MachineGateway } from "../models/machine";
import { cloneDeep } from "lodash";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import {
  IMachineEditFormModel,
  vsComponentName as machineEditFormComponentName,
} from "../components/machineEditForm";
import { vsComponentName as MoveComponentName } from "../views/machines/detail/configurations/move";
import { vsComponentName as DecommissionComponentName } from "../views/machines/detail/configurations/decommission";
import { vsComponentName as WarehouseComponentName } from "../views/machines/detail/configurations/warehouse";
import { vsComponentName as machinesMoveComponentName } from "../views/machines/move/component";
export function MachineChangeChildStateProviderConfig(
  multiProvider: MultipleMachineChangeChildStateServiceProvider,
  singleProvider: SingleMachineChangeChildStateServiceProvider
) {
  multiProvider
    .addElement({
      displayName: "Clone",
      id: "clone",
      cssClass: "fa fa-clone",
      requiredPermission: "machineClone",
      uiSrefFragment: "add",
    })
    .addElement({
      displayName: "Convert",
      id: "conversion",
      cssClass: "fa fa-cogs",
      requiredPermission: "machineConvert",
      uiSrefFragment: "main.machines.detail.configurations.conversion",
      absoluteSref: true,
    })
    .addElement({
      displayName: "Move",
      id: "moves",
      cssClass: "fa fa-arrows",
      requiredPermission: "machineMove",
      uiSrefFragment: "moves",
      stateDefinition: {
        url: "/moves",
        component: machinesMoveComponentName,
        params: {
          machineConfigurations: { value: [] },
        },
        resolve: {
          machineConfigurations: [
            "$stateParams",
            function ($stateParams: StateParams) {
              return $stateParams.machineConfigurations;
            },
          ],
        },
        bindings: {
          machineConfigurations: "machineConfigurations",
        },
      },
    })
    .addElement({
      displayName: "Warehouse",
      id: "warehouse",
      cssClass: "fa fa-archive",
      requiredPermission: "machineWarehouse",
      uiSrefFragment: "warehouses",
      stateDefinition: {
        url: "/warehouses",
        component: "machinesWarehouseView",
        params: {
          machineConfigurations: { value: [] },
        },
        resolve: {
          machineConfigurations: [
            "$stateParams",
            function ($stateParams: StateParams) {
              return $stateParams.machineConfigurations;
            },
          ],
        },
      },
    })
    .addElement({
      displayName: "Decommission",
      id: "decommission",
      cssClass: "fa fa-recycle",
      requiredPermission: "machineWarehouse",
      uiSrefFragment: "decommissions",
      stateDefinition: {
        url: "/decommissions",
        component: MachinesDecommissionViewComponentName,
        params: {
          machineConfigurations: { value: [] },
        },
        resolve: {
          machineConfigurations: [
            "$stateParams",
            function ($stateParams: StateParams) {
              return $stateParams.machineConfigurations;
            },
          ],
        },
        bindings: {
          model: "model",
        },
      },
    });

  singleProvider
    .addElement({
      displayName: "Clone",
      id: "clone",
      cssClass: "fa fa-clone",
      requiredPermission: "machineEdit",
      uiSrefFragment: "main.machines.add",
      absoluteSref: true,
    })
    .addElement({
      displayName: "Convert",
      id: "conversion",
      cssClass: "fa fa-cogs",
      requiredPermission: "machineConvert",
      uiSrefFragment: "conversion",
      stateDefinition: {
        url: "/conversion",
        component: machineEditFormComponentName,
        params: {
          modelMachineConfigurationId: undefined,
          modelMachineId: undefined,
        },
        resolve: {
          model: [
            "machine",
            "$stateParams",
            CloneAndConversionServiceName,
            (
              machine: MachineGateway,
              $stateParams: StateParams,
              cloneAndConversionService: CloneAndConversionServiceGateway
            ) => {
              let modelMachineId: number | undefined =
                $stateParams.modelMachineId;
              let modelMachineConfigurationId: number | undefined =
                $stateParams.modelMachineConfigurationId;

              const currentConfiguration = machine.Configs!.find(
                (c) => c.CurrentConfiguration
              )!;

              if (
                modelMachineConfigurationId == undefined &&
                modelMachineConfigurationId !== undefined
              )
                throw new Error("Must provide model machine configuration id");

              //if no model provided, copy current machine
              if (
                modelMachineId === undefined &&
                modelMachineConfigurationId === undefined
              ) {
                modelMachineId = currentConfiguration.MachineId;
                modelMachineConfigurationId = currentConfiguration.Id;
              }

              const modelMachine = cloneDeep(machine ?? {});
              delete modelMachine.Configs;

              return cloneAndConversionService
                .getConversionConfiguration(
                  currentConfiguration,
                  modelMachineId,
                  modelMachineConfigurationId
                )
                .then((conversionConfiguration) => {
                  return {
                    Machine: modelMachine,
                    Configuration: conversionConfiguration,
                  };
                });
            },
          ],
          progressiveDefinition: [
            "model",
            CloneAndConversionServiceName,
            (
              model: IMachineEditFormModel,
              cloneAndConversionService: CloneAndConversionServiceGateway
            ) =>
              cloneAndConversionService.getProgressiveDefinition(
                model.Configuration.ProgressiveDefinitionId
              ),
          ],
          machineFieldsEditable: [() => false],
          onSubmit: [
            FormViewServiceName,
            "$state",
            (formViewService: FormViewService, $state: StateService) =>
              (
                formViewController: FormViewController,
                model: IMachineEditFormModel
              ) =>
                formViewService
                  .submitForm({
                    url: `/api/machines/${model.Machine.Id}/conversion`,
                    method: "post",
                    formObject: model.Configuration,
                    formViewController,
                    validationPrefix: "Configuration",
                  })
                  .then((result: unknown) =>
                    $state.go("^", undefined, { reload: true })
                  ),
          ],
          onCancel: ["$state", ($state: StateService) => () => $state.go("^")],
          formTitle: [() => "Convert Machine"],
        },
      },
    })
    .addElement({
      displayName: "Move",
      id: "move",
      cssClass: "fa fa-arrows",
      requiredPermission: "machineMove",
      uiSrefFragment: "move",
      stateDefinition: {
        component: MoveComponentName,
        url: "/move",
      },
    })
    .addElement({
      displayName: "Warehouse",
      id: "warehouse",
      cssClass: "fa fa-archive",
      requiredPermission: "machineWarehouse",
      uiSrefFragment: "warehouse",
      stateDefinition: {
        component: WarehouseComponentName,
        url: "/warehouse",
      },
    })
    .addElement({
      displayName: "Decommission",
      id: "decommission",
      cssClass: "fa fa-recycle",
      requiredPermission: "machineDecommission",
      uiSrefFragment: "decommission",
      stateDefinition: {
        component: DecommissionComponentName,
        url: "/decommission",
        resolve: {
          machine: ["machine", (machine: any) => machine],
        },
        bindings: {
          machine: "machine",
        },
      },
    });
}
MachineChangeChildStateProviderConfig.$inject = [
  MultipleMachineChangeProviderName,
  SingleMachineChangeProviderName,
];
